/**
 * F4 (#1858): what an answer in progress shows — read from the server's frames only, never built by the client.
 *
 * - The reading ("Reading this as" / "Read as"): `mission_planned.reading` (the plan's, one part per query step), each
 *   part replaced by its `part_accepted.reading` once accepted (what ran, `source: ran`); before the plan, the early
 *   `answer_progress {stage: reading}` (live only, on `turn.progress`). Every label is the server's, rendered for this
 *   reader; the client never names a column.
 * - The progress line: `mission_planned.progress` (its steps' words, "usually about N s" and "taking longer than
 *   usual", all the server's), the current step read from today's frames. Before the plan: "Reading your question".
 * - The frame of a one-part answer, its rows only once `part_accepted {accepted: true}` names the part.
 * - "Took N s": `answer_timed.elapsed_ms`, the server's clock.
 */
import type { LandingAgentEvent, LandingAgentTurn, WorkerResultSnapshot } from './landingAgentSlice';
import { PART } from './acceptance';

/** A worker snapshot of an F4 answer, held until code accepts its part (`part_accepted`): never shown, never counted. */
export const PENDING_ACCEPTANCE = 'pending_acceptance';
/** The words before the plan (the server's routed frame says the same). */
export const BEFORE = 'Reading your question';

export interface Labelled { id: string; label: string; draft?: boolean }
export interface ReadingWindow { start: string; end: string; said?: string; label: string }
export interface ReadingFilter { dimension: string; operator: string; value: unknown; label: string }
/** One item F3 decided for the part (#1808), as the server rendered it for this reader. */
export interface ReadingDefault {
  id: string; kind: string; label: string; sentence: string; by?: string; changeable?: boolean;
  options?: { option: number; label: string; current?: boolean }[];
}
export interface ReadingPart {
  sub_task_id: string;
  source: 'plan' | 'ran';
  result_id?: string;
  perspective: Labelled;
  metrics: Labelled[];
  grouping: Labelled[];
  window: ReadingWindow | null;
  filters: ReadingFilter[];
  defaults?: ReadingDefault[];
  /** The part was not accepted (`part_accepted {accepted: false}`): it is not answered. */
  notAnswered?: boolean;
}

/** `answer_progress` as the turn holds it: live only (never in its events, never on the thread snapshot). */
export interface LiveProgress {
  words?: string;
  usually?: string;
  longer?: string;
  longer_after_s?: number;
  parts?: ReadingPart[];
}

export interface ProgressStep {
  id: string; kind: 'find' | 'summary'; sub_task_ids: string[];
  words: { now: string; checking?: string; retry?: string };
}

const isObj = (v: unknown): v is Record<string, any> => !!v && typeof v === 'object' && !Array.isArray(v);
const labelled = (v: unknown): v is Labelled => isObj(v) && typeof v.id === 'string' && typeof v.label === 'string';

/** A rendered reading part as the server sent it; null for anything else (a stored, label-less part included). */
export function readingPart(value: unknown): ReadingPart | null {
  if (!isObj(value) || typeof value.sub_task_id !== 'string' || (value.source !== 'plan' && value.source !== 'ran')) return null;
  if (!labelled(value.perspective) || !Array.isArray(value.metrics) || !value.metrics.length || !value.metrics.every(labelled)) return null;
  const grouping = Array.isArray(value.grouping) ? value.grouping.filter(labelled) : [];
  const w = value.window;
  const window = isObj(w) && typeof w.start === 'string' && typeof w.end === 'string' && typeof w.label === 'string'
    ? { start: w.start, end: w.end, label: w.label, ...(typeof w.said === 'string' ? { said: w.said } : {}) } : null;
  const filters = (Array.isArray(value.filters) ? value.filters : [])
    .filter((f: any) => isObj(f) && typeof f.label === 'string' && typeof f.dimension === 'string');
  const defaults = (Array.isArray(value.defaults) ? value.defaults : [])
    .filter((d: any) => isObj(d) && typeof d.id === 'string' && typeof d.label === 'string' && typeof d.sentence === 'string');
  return { sub_task_id: value.sub_task_id, source: value.source, ...(typeof value.result_id === 'string' ? { result_id: value.result_id } : {}),
    perspective: value.perspective, metrics: value.metrics, grouping, window, filters, ...(defaults.length ? { defaults } : {}) };
}

/** The live `answer_progress` frame folded onto what the turn holds: the routed words and timing, then the early reading. */
export function liveProgressAfter(held: LiveProgress | null | undefined, evt: LandingAgentEvent): LiveProgress | null {
  if (evt.type !== 'answer_progress') return null;
  if (evt.stage === 'routed') {
    return { ...(held || {}), ...(typeof evt.words === 'string' ? { words: evt.words } : {}),
      ...(typeof evt.usually === 'string' ? { usually: evt.usually } : {}),
      ...(typeof evt.longer === 'string' ? { longer: evt.longer } : {}),
      ...(typeof evt.longer_after_s === 'number' ? { longer_after_s: evt.longer_after_s } : {}) };
  }
  if (evt.stage === 'reading') {
    const parts = (Array.isArray(evt.parts) ? evt.parts : []).map(readingPart).filter((p): p is ReadingPart => !!p);
    return parts.length ? { ...(held || {}), parts } : held || null;
  }
  return null;
}

/** The answer's plan as F4 sends it (`mission_planned` with its `progress` block); null for an answer from before F4. */
export function plannedOf(turn: Pick<LandingAgentTurn, 'events'>): LandingAgentEvent | null {
  const planned = [...(turn.events || [])].reverse().find((e) => e?.type === 'mission_planned');
  return planned && isObj(planned.progress) ? planned : null;
}

/** Whether this answer is one F4 shows (its plan carries the progress block, or the server timed it). */
export function isProgressAnswer(turn: Pick<LandingAgentTurn, 'events'>): boolean {
  return !!plannedOf(turn) || (turn.events || []).some((e) => e?.type === 'answer_timed');
}

/** Each part's decision (`part_accepted`), by sub-task: true accepted, false not. */
export function decisions(events: LandingAgentEvent[]): Map<string, LandingAgentEvent> {
  const out = new Map<string, LandingAgentEvent>();
  for (const e of events || []) if (e?.type === PART && typeof e.sub_task_id === 'string') out.set(e.sub_task_id, e);
  return out;
}

/** The reading the turn shows: the plan's parts, each replaced by what ran once accepted; before the plan, the early one.
 *  A part the plan's reading dropped for this reader but that ran is shown as it ran (the server rendered it for them). */
export function readingOf(turn: Pick<LandingAgentTurn, 'events' | 'progress'>): ReadingPart[] {
  const planned = plannedOf(turn);
  const decided = decisions(turn.events || []);
  const plan: ReadingPart[] = planned ? (Array.isArray(planned.reading?.parts) ? planned.reading.parts : []).map(readingPart)
    .filter((p: ReadingPart | null): p is ReadingPart => !!p) : (turn.progress?.parts || []);
  const shown = plan.map((part) => {
    const decision = decided.get(part.sub_task_id);
    if (!decision) return part;
    if (decision.accepted !== true) return { ...part, notAnswered: true };
    // what ran, as the server rendered it for this reader; a part it dropped for them is dropped here too
    return readingPart(decision.reading);
  });
  const ran = Array.from(decided.values()).filter((d) => d.accepted === true && !plan.some((p) => p.sub_task_id === d.sub_task_id))
    .map((d) => readingPart(d.reading));
  return [...shown, ...ran].filter((p): p is ReadingPart => !!p);
}

export function stepsOf(turn: Pick<LandingAgentTurn, 'events'>): ProgressStep[] {
  const steps = plannedOf(turn)?.progress?.steps;
  return (Array.isArray(steps) ? steps : []).filter((s: any) => isObj(s) && (s.kind === 'find' || s.kind === 'summary')
    && isObj(s.words) && typeof s.words.now === 'string' && Array.isArray(s.sub_task_ids));
}

export interface ProgressNow { words: string; step: number | null; of: number | null; usually: string | null; longer: string | null; longerAfter: number | null }

/** The progress line's parts: the current step's words (the server's), "step i of n", and the usual time. */
export function progressOf(turn: Pick<LandingAgentTurn, 'events' | 'progress'>): ProgressNow {
  const planned = plannedOf(turn);
  const live = turn.progress || null;
  const timing = planned?.progress || live || {};
  const usually = typeof timing.usually === 'string' ? timing.usually : null;
  const longer = typeof timing.longer === 'string' ? timing.longer : null;
  const longerAfter = typeof timing.longer_after_s === 'number' ? timing.longer_after_s : null;
  const steps = stepsOf(turn);
  if (!steps.length) return { words: (live?.words || BEFORE), step: null, of: null, usually, longer, longerAfter };
  const events = turn.events || [];
  const decided = decisions(events);
  const summarising = events.some((e) => e?.type === 'synthesis_started'
    || (e?.type === 'sub_task_started' && e.worker_type === 'result_formatter'));
  const current = summarising ? steps.find((s) => s.kind === 'summary')
    : steps.find((s) => s.kind === 'find' && !decided.has(s.sub_task_ids[0])) || steps.find((s) => s.kind === 'summary');
  const at = current ? steps.indexOf(current) : steps.length - 1;
  const step = steps[at];
  let words = step.words.now;
  if (step.kind === 'find') {
    const sid = step.sub_task_ids[0];
    const started = events.map((e, i) => [e, i] as const).filter(([e]) => e?.type === 'sub_task_started' && e.sub_task_id === sid);
    const last = started[started.length - 1];
    const since = last ? events.slice(last[1] + 1) : [];
    if (since.some((e) => e?.type === 'worker_result' && e.sub_task_id === sid && e.status === 'done')) {
      words = step.words.checking || words;
    } else if (last && typeof last[0].attempt === 'number' && last[0].attempt > 1) {
      words = step.words.retry || words;
    }
  }
  return { words, step: at + 1, of: steps.length, usually, longer, longerAfter };
}

/** "Took N s": the server's own measure, whole seconds; null for an answer from before F4. */
export function tookOf(turn: Pick<LandingAgentTurn, 'events'>): number | null {
  const timed = [...(turn.events || [])].reverse().find((e) => e?.type === 'answer_timed');
  return timed && typeof timed.elapsed_ms === 'number' && timed.elapsed_ms >= 0 ? Math.round(timed.elapsed_ms / 1000) : null;
}

/** The sub-tasks code did not accept, in the order they were decided. */
export function notAccepted(turn: Pick<LandingAgentTurn, 'events'>): string[] {
  return Array.from(decisions(turn.events || []).values()).filter((d) => d.accepted !== true).map((d) => d.sub_task_id);
}

export interface AnswerFrameShape {
  /** Column heads, each the server's label: the grouping's (and a grouping F3 decided), then the metric's. */
  heads: { key: string; label: string; metric: boolean }[];
  /** The part's rows, only once code accepted it; null until then. */
  rows: Record<string, unknown>[] | null;
}

const bare = (id: string) => id.slice(id.lastIndexOf('.') + 1);

function headsOf(part: ReadingPart, decision?: LandingAgentEvent, columns: string[] = []): AnswerFrameShape['heads'] {
  // a grouping F3 decided (#1808) in place of the plain one it gave way to, headed by the server's own words for this
  // reader: a dimension by its chosen option, a fitted time grain by its item (its column is the one at that grain).
  // A value item (`value:…`) decided a filter's column, not a grouping: it adds no head.
  const decided = (Array.isArray(decision?.defaults) ? decision!.defaults : [])
    .filter((d: any) => isObj(d) && typeof d.id === 'string' && !d.id.startsWith('value:') && typeof d.used === 'string')
    .flatMap((d: any) => {
      const shown = (part.defaults || []).find((i) => i.id === d.id);
      if (!shown) return [];
      if (d.kind === 'grain') {
        const key = columns.find((c) => c.endsWith(`__${d.used}`));
        return key ? [{ key, label: shown.label, metric: false }] : [];
      }
      if (d.kind !== 'dimension') return [];
      const current = shown.options?.find((o) => o.current);
      return current ? [{ key: d.used as string, label: current.label, metric: false }] : [];
    });
  return [...part.grouping.map((g) => ({ key: g.id, label: g.label, metric: false })), ...decided,
    ...part.metrics.map((m) => ({ key: bare(m.id), label: m.label, metric: true }))];
}

/** The answer's shape before the final card: one query part only, its frame from the plan's reading, its rows (each
 *  column the server labelled) once `part_accepted {accepted: true}` names the part. */
export function frameOf(turn: Pick<LandingAgentTurn, 'events' | 'result'>): AnswerFrameShape | null {
  const finds = stepsOf(turn).filter((s) => s.kind === 'find');
  if (finds.length !== 1) return null;
  const sid = finds[0].sub_task_ids[0];
  const planned = plannedOf(turn);
  const planPart = (Array.isArray(planned?.reading?.parts) ? planned!.reading.parts : []).map(readingPart)
    .find((p: ReadingPart | null) => p?.sub_task_id === sid) as ReadingPart | undefined;
  const decision = decisions(turn.events || []).get(sid);
  if (decision && decision.accepted !== true) return null;
  const ranPart = decision ? readingPart(decision.reading) : null;
  const part = ranPart || planPart;
  if (!part) return null;
  const worker: WorkerResultSnapshot | undefined = (turn.result?.workerResults || [])
    .find((w) => w.sub_task_id === sid && w.status === 'done');
  if (!decision || !worker || !Array.isArray(worker.rows)) return { heads: headsOf(planPart || part), rows: null };
  const columns = worker.columns || (worker.rows[0] ? Object.keys(worker.rows[0]) : []);
  const heads = headsOf(part, decision, columns).filter((h) => columns.includes(h.key));
  if (!heads.some((h) => h.metric)) return { heads: headsOf(planPart || part), rows: null };
  return { heads, rows: worker.rows };
}

/** A turn as the thread snapshot keeps it: without the live progress frames and the in-memory "away" mark (§1a, §5). */
export function withoutLiveProgress<T extends { progress?: LiveProgress | null; wasAway?: boolean }>(turn: T): T {
  if (!turn || (!('progress' in turn) && !('wasAway' in turn))) return turn;
  const { progress, wasAway, ...rest } = turn;
  return rest as T;
}
